/**
 * 登录态与权限 store。
 *
 * 关键行为：
 *  - token 与 profile 分离存储：token 在 api/token.ts（后台与会员分开），profile 只在内存；
 *  - `permissions` 直接来自 `/admin/auth/profile`，**菜单据此动态生成**；
 *  - 接口不可用时 `profileError.network = true`，页面走降级展示而不是白屏。
 */
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import type { AdminLoginReq, AdminProfileRes } from '@weidian/contracts'
import { adminLogin, adminLogout, fetchAdminProfile } from '@/api/auth'
import { ApiError, toApiError } from '@/api/errors'
import { clearAdminTokens, getAdminAccessToken, setAdminTokens } from '@/api/token'
import { ensureAdminSession } from '@/api/request'
import { hasPermission } from '@/utils/permission'

export const useAuthStore = defineStore('admin-auth', () => {
  const profile = ref<AdminProfileRes | null>(null)
  const loading = ref(false)
  const profileLoaded = ref(false)
  /** 最近一次 profile 拉取失败的原因（用于降级提示） */
  const profileError = ref<ApiError | null>(null)
  /** 内存里的 token 视图（真正的持久化在 api/token.ts） */
  const accessToken = ref<string | null>(getAdminAccessToken())

  const loggedIn = computed(() => Boolean(accessToken.value || getAdminAccessToken()))
  const permissions = computed<readonly string[]>(() => profile.value?.permissions ?? [])
  const displayName = computed(() => profile.value?.realName || profile.value?.username || '未登录')
  const roleName = computed(() => profile.value?.roleName ?? '—')

  function syncTokenFromStorage(): void {
    accessToken.value = getAdminAccessToken()
  }

  async function login(payload: AdminLoginReq): Promise<void> {
    loading.value = true
    try {
      const res = await adminLogin(payload)
      // 后端登录响应是**扁平结构**：管理员信息与 token 同级，这里拆成 token + profile 两份状态。
      // 后端不发 refreshToken（后台 token 有效期 2h，过期需重新登录）。
      setAdminTokens(res.accessToken)
      accessToken.value = res.accessToken
      profile.value = {
        id: res.id,
        username: res.username,
        realName: res.realName,
        roleCode: res.roleCode,
        roleName: res.roleName,
        permissions: res.permissions,
      }
      profileLoaded.value = true
      profileError.value = null
    } finally {
      loading.value = false
    }
  }

  /** 拉取 profile。失败不抛出（避免守卫里 401 之外的原因把用户弹出去） */
  async function ensureProfile(force = false): Promise<AdminProfileRes | null> {
    if (profileLoaded.value && !force) return profile.value
    if (!getAdminAccessToken()) {
      /**
       * ★★ **启动自愈**（2026-09-29）：access token 存在 sessionStorage
       *    （每个标签页一份、关掉标签页就没了），而 refresh token 在 localStorage。
       *    所以"重开标签页 / 重启浏览器"时的常见状态就是**有 refresh、没有 access**。
       *
       *    过去这里直接 return null → 路由守卫判定未登录 → 跳登录页，
       *    用户感受就是"关个标签页再进来又要重新登录"（这也是"总是掉线"的主因之一）。
       *    现在先静默续期把会话接回来；refresh 也没有/已失效才真的按未登录处理。
       */
      const revived = await ensureAdminSession()
      if (!revived) {
        profileLoaded.value = false
        return null
      }
    }
    loading.value = true
    try {
      const res = await fetchAdminProfile()
      profile.value = res
      profileLoaded.value = true
      profileError.value = null
      return res
    } catch (e) {
      const err = toApiError(e)
      profileError.value = err
      // 401 交给 request 层清 token 并跳登录，这里只标记未加载
      if (err.code === 1002 || err.code === 1107) {
        profileLoaded.value = false
      }
      return null
    } finally {
      loading.value = false
      syncTokenFromStorage()
    }
  }

  async function logout(): Promise<void> {
    try {
      if (getAdminAccessToken()) await adminLogout()
    } catch {
      // 登出失败也必须清本地态，否则用户被卡住
    } finally {
      clearAdminTokens()
      accessToken.value = null
      profile.value = null
      profileLoaded.value = false
      profileError.value = null
    }
  }

  function can(required?: string | string[]): boolean {
    return hasPermission(permissions.value, required)
  }

  return {
    profile,
    loading,
    profileLoaded,
    profileError,
    accessToken,
    loggedIn,
    permissions,
    displayName,
    roleName,
    login,
    logout,
    ensureProfile,
    can,
    syncTokenFromStorage,
  }
})
